// Async boundary for Module Federation 2.0
// The dynamic import creates the async boundary that Module Federation
// needs to negotiate shared dependencies at runtime before the app boots.
//
// We also await `window.__envReady` here — the promise started by the inline
// loader in index.html that fetches /env and assigns window._env. Awaiting it
// before importing ./bootstrap guarantees the runtime environment config is in
// place before any app module (starting with src/instrument.ts, the Sentry
// side-effect import) evaluates and reads it via getEnvVar.
async function start(): Promise<void> {
  try {
    await window.__envReady;
  } catch (error) {
    // The loader already logs + falls back to {}. Never block boot on env load.
    console.error('Runtime env config failed to load; continuing with defaults', error);
  }

  // Telemetry starts after the env gate above and before the app boots, so fetch/XHR are instrumented
  // before any app module issues a request.
  //
  // The DYNAMIC import is load-bearing, not stylistic: a static `import { initObservability } from
  // './observability'` at the top of this entry module would evaluate that module's import graph —
  // including src/config.ts, which reads getEnvVar at module scope — before start() ever runs. Every
  // flag would resolve to its default and telemetry would report itself disabled on every load, with
  // nothing to indicate why. Same reason `./bootstrap` below is imported dynamically.
  try {
    const { initObservability } = await import('./observability');
    initObservability();
  } catch (error) {
    // A telemetry chunk that fails to load must not stop the application from starting.
    console.error('Observability failed to initialise; continuing without telemetry', error);
  }

  await import('./bootstrap');
}

void start();

export {};
